export type Page<T> = {
  items: Array<T>
  totalItems: number
  totalPages: number
}

export type Company = {
  name: string
  companyId: number
}

export type Category = {
  id: string
  name: string
}

export type Employee = {
  id: string
  firstName: string
  surname: string
  company: Company
  dateOfBirth: Date
  ssn: number
  addressLine: string
  postalPlace: PostalPlace
  employeeNo: string
  jobDescription: string
  employmentType?: EmploymentType
  employmentPercentage?: string
  employmentPercentageDate?: Date
  hireStartDate: Date
  hireEndDate?: Date
  isOnWaitPay: boolean
  waitPayDate: Date
  paidPercentage?: number
  paidPercentageDate?: Date
  ticketHolderCode: string
  preferredProduct?: Category
  abstainFromTicket: boolean
  createdAt: Date
  createdBy: string
  leaveFromDate?: Date
  isDeleted: boolean
  lastUpdate: Date
  sourceLastUpdate: string
  sensitiveInfo?: SensitiveEmployeeInfo
  categories: YearlyCategory[]
  yearlyFees: YearlyFee[]
  ticketRights?: TicketRight[]
  customerNumber?: number
  customerPreferences?: CustomerPreference
  draftEmployee?: Employee
  payType?: PayType
  disabilityGrade?: number
  disabilityFromDate?: Date
  hireEndCause?: EmploymentEndCause
  disabilityProofs?: YearlyDisabilityProof[]
}

export type SensitiveEmployeeInfo = {
  payType?: PayType
  disabilityGrade?: number
  disabilityFromDate?: Date
  hireEndCause?: EmploymentEndCause
  disabilityProofs: YearlyDisabilityProof[]
  draftEmployee?: SensitiveEmployeeInfo
  note?: string
}

export type AcceptDraftResponse = {
  nonSensitiveEmployeeDataResponse: Employee
  sensitiveEmployeeDataResponse: SensitiveEmployeeInfo
  familyMemberResponse: FamilyMember[]
}

export type YearlyCategory = {
  year: number
  category: Category
}

export type YearlyFee = {
  year: number
  isPaid: boolean
}

export type YearlyDisabilityProof = {
  year: number
  isRegistered: boolean
}

export type YearlyLowIncome = {
  year: number
  isRegistered: boolean
}

export type CouponsLimit = {
  year: number
  couponsLimit: number
}

export type CouponsStatus = {
  totalCoupons: number
  remainingCoupons: number
}

export type PayType = {
  code: string
  description: string
}

export type EmploymentEndCause = {
  code: string
  description: string
}

export type EmploymentType = {
  code: string
  description: string
}

export type FamilyRelation = {
  code: string
  description: string
}

export type PostalPlace = {
  code: string
  town: string
}

export type SimpleRule = {
  id: number
  name: string
}

export type TicketType = {
  id: number
  code: string
  description: string
  countryCode: string
  generatesTicketRights: boolean
  canHaveStationPairs: boolean
  isDeleted?: boolean
}

export type Station = {
  id: string // Netex format
  name?: string
}

export type StationPair = {
  id: number
  fromStation: Station
  toStation: Station
}

export type StationPairRequest = {
  fromStation: string
  toStation: string
}

export type StopPlaces = {
  features: Array<StopPlace>
}

export type StopPlace = {
  properties: {
    id: string
    name: string
    category: [string]
  }
}

export type FamilyMember = {
  id: string
  firstName: string
  surname: string
  relation: FamilyRelation
  dateOfBirth: string
  ssn?: string
  ticketHolderCode: string
  abstainFromTicket: boolean
  lowIncomes?: YearlyLowIncome[]
  ticketRights?: TicketRight[]
  draftTicketRights?: TicketRight[]
  customerNumber?: number
  customerPreferences?: CustomerPreference
  createdAt: Date
  createdBy: string
  updatedAt?: Date
  updatedBy?: string
}

export type TicketRightHolder = {
  id: string
  firstName: string
  surname: string
  employeeNo: string | undefined
  companyName: string | undefined
  hireEndDate: string | undefined
  draftHireEndDate: string | undefined
  isEmployee: boolean
  dateOfBirth: string
  isDeleted: boolean
}

export type DeprecatedTicketRightHolder = {
  id: number
  ticketHolderCode: string
}

export enum TicketTypePreference {
  ADULT = 'ADULT',
  SENIOR_CITIZEN = 'SENIOR_CITIZEN',
  STUDENT = 'STUDENT',
}

export type CustomerPreference = {
  ticketTypePreference: TicketTypePreference
}

export type ManualOrder = {
  id: number
  ticketType: TicketType
  issueDate: Date
  fromDate: string
  toDate: string
  stations?: StationPair[]
}

export type TicketRight = {
  ticketrightId: number
  ticketHolderId: number
  ticketRightsHolder: DeprecatedTicketRightHolder
  employeeId: number
  validFrom: string
  validUntil: string
  ticketType: TicketType
  note: string
  ticketCreated: Date
  errorMessage: string
  createdByRule?: SimpleRule
  calculationTimestamp: Date
  legacySyncStatus?: SyncStatus
  essSyncStatus?: SyncStatus
  manualOrder?: ManualOrder
}

export type FipTicketTypeSimple = {
  id: number
  code: string
}

export type TravelType = {
  id: number
  code: string
  description?: string
}

export type FipTicket = {
  ticketId: number
  fipTicketTypeSimple?: FipTicketTypeSimple
  issueDate: Date
  ticketNo: number
  validFrom: Date
  validUntil: Date
  travelType?: TravelType
  employeeNo: string
  firstName: string
  surname: string
  isDeleted: boolean
  note: string
  familyMemberId: string
}

export type SyncStatus = {
  isReadyToSync: boolean
  syncTimestamp: Date
  errorMessage: string
}

export type PostalCode = {
  postalCode: string
  town: string
}

export enum FetchState {
  NOT_STARTED,
  LOADING,
  SUCCESS,
  BLOB_SUCCESS,
  ERROR,
  SUCCESS_NO_CONTENT,
}

export type Fetch<T> =
  | { status: FetchState.NOT_STARTED }
  | { status: FetchState.LOADING }
  | { status: FetchState.ERROR; error: unknown }
  | { status: FetchState.SUCCESS; data: T }
  | {
      status: FetchState.BLOB_SUCCESS
      blob: Blob
      fileName: string | undefined
    }
  | { status: FetchState.SUCCESS_NO_CONTENT }

export type HttpResponse<T> =
  | { contentType: 'json'; json: T }
  | { contentType: 'text/html'; text: T }
  | { contentType: 'blob'; blob: Blob; fileName: string | undefined }
  | { contentType: 'unknown' }
  | { contentType: 'no-content'; nothing: T }

export type HttpRequest = {
  id?: string
  query?: string
  year?: string
  body?: Record<string, unknown>
}

export type AuthenticatedHttpRequest = {
  token: string
  id?: string
  year?: string
  query?: string
  body?: Record<string, unknown>
}

export type ClientInfo = {
  hasSensitiveAccess?: boolean
}

export type SavingDetails = {
  ticketPrice: string
  additionalProduct?: string
  refund?: string
  saving: string
}

export type MonthlySaving = {
  month: number
  year: number
  details: SavingDetails
}

export type SavingTicket = {
  name?: string
  travelDate: Date
  lastTravelDate: Date
  pathDescription: string
  orderId: string
  orderLineId: string
  isCancelled: boolean
  effectiveCancellationDate?: Date
  timestamp: Date
  isWorkTrip: boolean
  savingDistribution: MonthlySaving[]
  totalSaving: SavingDetails
}

export type EmployeeTickets = {
  firstName: string
  surname: string
  ticketHolderCode: string
  saving: string
  travels: SavingTicket[]
}

export type SavingTicketResponse = {
  travelDate: string
  lastTravelDate: string
  pathDescription: string
  orderId: string
  orderLineId: string
  isCancelled: boolean
  effectiveCancellationDate?: string
  timestamp: string
  isWorkTrip: boolean
  savingDistribution: MonthlySaving[]
  totalSaving: SavingDetails
}

export type EmployeeTripsResponse = {
  firstName: string
  surname: string
  ticketHolderCode: string
  saving: string
  travels: SavingTicketResponse[]
}
